/*
  Dirty Penny fast cart quick-add.

  The theme's default add-to-cart flow bundles a full cart-drawer render into the
  /cart/add.js response, replaces #CartDrawer's entire innerHTML, and then re-runs
  loadUpsell (one recommendations request per cart item, serially). That is why
  upsell quick-adds felt slow and flashed.

  This handler does the minimum instead:
    1. POST /cart/add.js with just the variant (fast, no sections bundle).
    2. ONE section fetch, then surgically swap only the regions that change:
       #dp-cart-title (count), #dp-cart-items-region, #dp-cart-footer-region,
       plus the header cart bubbles.
  The upsell rail (#mini-cart-upsell) is never touched: no re-fetch, no image
  reload, no Alpine teardown. Alpine auto-initializes the injected subtrees via
  its mutation observer — the same mechanism the theme's own re-render relies on.
*/
if (!window.Eurus) window.Eurus = { loadedScript: new Set() };
if (!window.Eurus.loadedScript.has('dp-cart.js')) {
  window.Eurus.loadedScript.add('dp-cart.js');

  (() => {
    const DRAWER_REGIONS = ['#dp-cart-title', '#dp-cart-items-region', '#dp-cart-footer-region'];
    const BUBBLES = [
      { id: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
      { id: 'mobile-cart-icon-bubble', selector: '#mobile-cart-icon-bubble' },
      { id: 'cart-icon-bubble-mobile-dock', selector: '#cart-icon-bubble-mobile-dock' }
    ];
    const SECTION_IDS = ['cart-drawer'].concat(BUBBLES.map(b => b.id));

    const sleep = (ms) => new Promise(r => setTimeout(r, ms));

    async function acquireCartLock() {
      // Same mutex the theme uses (window.updatingCart) so we never race
      // a quantity change or another add.
      for (let i = 0; i < 60 && window.updatingCart; i++) await sleep(50);
      window.updatingCart = true;
    }

    function innerOf(sectionHTML, selector) {
      const el = new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector(selector);
      return el ? el.innerHTML : null;
    }

    // The element that actually scrolls the items differs by breakpoint:
    // desktop = the items region itself; mobile = the outer drawer container
    // (single-scroller mirrorwater layout).
    function activeScroller() {
      const region = document.querySelector('#dp-cart-items-region');
      if (region && region.scrollHeight > region.clientHeight + 1) return region;
      return document.querySelector('#CartDrawer .border-t.overflow-auto.flex-1') || region;
    }

    function swapRegions(sections) {
      const fresh = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const scroller = activeScroller();
      const savedTop = scroller ? scroller.scrollTop : 0;
      const savedHeight = scroller ? scroller.scrollHeight : 0;
      // Per-item loading overlays carry `loading-<item.key>` ids — snapshot them
      // so rows that appear after the swap can be animated in as new. Match on
      // the variant half of the key only: Shopify re-hashes EVERY line's key
      // when the cart's discount state changes (e.g. crossing the "add 2 more
      // to unlock 5% off" tier), and on the full key that add re-animated the
      // whole list instead of the one new row.
      const rowKey = (id) => id.replace(/^loading-/, '').split(':')[0];
      const prevItemIds = new Set(
        [...document.querySelectorAll('#dp-cart-items-region [id^="loading-"]')].map(el => rowKey(el.id))
      );

      DRAWER_REGIONS.forEach(sel => {
        const cur = document.querySelector(sel);
        const next = fresh.querySelector(sel);
        if (cur && next) cur.innerHTML = next.innerHTML;
      });
      // Keep the user's view anchored: new items PREPEND above the fold, so when
      // scrolled (e.g. looking at the mobile picks carousel) compensate for the
      // height change — the content under their thumb must not move.
      if (scroller) {
        if (savedTop > 0) {
          const delta = scroller.scrollHeight - savedHeight;
          scroller.scrollTop = Math.max(0, savedTop + delta);
        } else {
          scroller.scrollTop = 0;
        }
      }

      document.querySelectorAll('#dp-cart-items-region [id^="loading-"]').forEach(el => {
        if (!prevItemIds.has(rowKey(el.id))) el.closest('tr')?.classList.add('dp-item-new');
      });

      BUBBLES.forEach(({ id, selector }) => {
        const cur = document.querySelector(selector);
        if (cur && sections[id]) {
          const html = innerOf(sections[id], selector);
          if (html !== null) cur.innerHTML = html;
        }
      });
    }

    function setDimmed(on) {
      DRAWER_REGIONS.forEach(sel => {
        document.querySelector(sel)?.classList.toggle('dp-region-updating', on);
      });
    }

    // Wholesale drawer replacement (layout switches: empty <-> full). Alpine
    // re-initializes the injected tree via its mutation observer. The naive
    // innerHTML swap reads like a page refresh (the backdrop's blur entrance
    // animation replays and the panel flashes) — suppress the overlay
    // re-animation and crossfade the panel instead.
    function fullDrawerReplace(sections) {
      const html = sections && sections['cart-drawer'];
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
      const cur = document.querySelector('#CartDrawer');
      if (fresh && cur) {
        cur.innerHTML = fresh.innerHTML;
        const overlay = cur.querySelector('#CartDrawer-Overlay');
        let isOpen = false;
        try { isOpen = !!(window.Alpine && Alpine.store('xMiniCart') && Alpine.store('xMiniCart').open); } catch (err) { /* stores not ready */ }
        // Suppress the backdrop's entrance only when the drawer is ALREADY
        // open (last item removed -> empty layout): replaying it then reads
        // as a refresh. Replacing a closed drawer (first add into an empty
        // cart) must keep it — the drawer opens a beat later and that fade
        // IS the opening.
        if (overlay && isOpen) {
          overlay.style.animation = 'none';
          // ...but only for THIS open: left in place, the inline none killed
          // the backdrop fade for every later open of the session. Hand the
          // animation back once the drawer has closed (x-show sets
          // display:none) — from there it restarts on the next open, which
          // is exactly when it should play.
          const giveBack = new MutationObserver(() => {
            if (cur.style.display !== 'none') return;
            overlay.style.animation = '';
            giveBack.disconnect();
          });
          giveBack.observe(cur, { attributes: true, attributeFilter: ['style'] });
        }
        const panel = cur.querySelector('#update-cart');
        // same rule for the panel crossfade: closed drawers get the real
        // x-transition entrance when they open a beat later
        if (panel && isOpen) {
          panel.style.opacity = '0';
          panel.style.transition = 'opacity 0.3s ease';
          requestAnimationFrame(() => requestAnimationFrame(() => { panel.style.opacity = '1'; }));
          setTimeout(() => { panel.style.transition = ''; panel.style.opacity = ''; }, 600);
        }
      }
      BUBBLES.forEach(({ id, selector }) => {
        const el = document.querySelector(selector);
        if (el && sections[id]) {
          const inner = innerOf(sections[id], selector);
          if (inner !== null) el.innerHTML = inner;
        }
      });
    }

    /*
      Try to apply a sections payload surgically. Returns false when the swap
      isn't safe — e.g. the cart just became empty (fresh HTML renders the
      empty-state branch without our region ids) — so the caller can fall back
      to the theme's full re-render.
      The dim class is applied with transitions suppressed in the same
      synchronous block as the swap, so the new content paints at 35% opacity
      and only the fade back UP animates.
    */
    function surgicalApply(sections, quiet) {
      if (!sections || !sections['cart-drawer']) return false;
      const fresh = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      for (const sel of DRAWER_REGIONS) {
        if (!document.querySelector(sel) || !fresh.querySelector(sel)) return false;
      }
      const regions = DRAWER_REGIONS.map(sel => document.querySelector(sel));
      // The dim-and-fade-back is feedback for the theme's own re-renders
      // (quantity, remove, discount) which otherwise change silently. A
      // quick-add already has its ✓ on the button and the new row sliding
      // in — dimming the whole list on top of that read as a blink.
      if (!quiet) {
        regions.forEach(el => {
          el.style.transition = 'none';
          el.classList.add('dp-region-updating');
        });
      }
      swapRegions(sections);
      if (!quiet) {
        regions.forEach(el => { el.style.transition = ''; });
        requestAnimationFrame(() => setDimmed(false));
      }
      // Safety net: theme code that runs AFTER reRenderSections (updateCartUI)
      // has unguarded DOM reads; if it throws, xCart.loading sticks true and its
      // :class leaves pointer-events-none on the items region — clicks go dead.
      // The update is over by now, so force-clear a stuck loading flag.
      setTimeout(() => {
        try {
          const host = document.querySelector('[x-data="xCart"]');
          if (host && window.Alpine && Alpine.$data) {
            const data = Alpine.$data(host);
            if (data && data.loading) data.loading = false;
          }
        } catch (err) { /* best-effort */ }
      }, 800);
      return true;
    }

    /*
      Route the theme's cart re-renders (quantity +/-, remove, discount codes,
      note save, bundles...) through the surgical swap. Every caller passes the
      sections payload it already got back from cart/change.js|add.js|update.js,
      so this adds zero extra requests — it only stops the wholesale #CartDrawer
      innerHTML replacement (Alpine teardown, upsell re-fetch, image reloads).
      Must stay synchronous up to the swap when `sections` is provided:
      updateCartUI reads #cart-icon-bubble right after calling this.
    */
    /*
      The drawer is not in the first HTML: the header mounts it lazily
      (renderAjax on the first mouseover/touchstart). A quick-add that lands
      before that fetch returns — the very first touch on a phone IS the add
      button — has no #CartDrawer to update and openCart() shows nothing.
      Worse, the lazy GET was issued before the add, so when it does arrive
      it paints the pre-add cart. Two guards: mount the drawer ourselves from
      the add response's own section render, and stop the lazy fetch from
      overwriting a drawer that already exists.
    */
    function drawerMount() {
      return document.querySelector('[\\@init-run\\.window*="cart-drawer"]')
        || document.querySelector('[x-on\\:init-run\\.window*="cart-drawer"]');
    }

    function mountDrawer(sections) {
      const html = sections && sections['cart-drawer'];
      const mount = drawerMount();
      if (!html || !mount || document.querySelector('#CartDrawer')) return false;
      const inner = innerOf(html, '#ajax-loading-cart');
      if (inner === null) return false;
      mount.innerHTML = inner;
      BUBBLES.forEach(({ id, selector }) => {
        const el = document.querySelector(selector);
        if (el && sections[id]) {
          const bubble = innerOf(sections[id], selector);
          if (bubble !== null) el.innerHTML = bubble;
        }
      });
      return true;
    }

    function patchRenderAjax() {
      if (!(window.Alpine && Alpine.store && Alpine.store('xHeaderMenu'))) return false;
      const menu = Alpine.store('xHeaderMenu');
      if (menu.__dpGuard || typeof menu.renderAjax !== 'function') return !!menu.__dpGuard;
      menu.__dpGuard = true;
      const original = menu.renderAjax;
      menu.renderAjax = function (el, id, element) {
        if (id !== 'cart-drawer') return original.call(this, el, id, element);
        // Same fetch as the theme's, but a drawer mounted in the meantime
        // (by a quick-add) is fresher than this response — keep it.
        return fetch(`${window.location.pathname}?sections=${id}`)
          .then(r => r.json())
          .then(res => {
            if (el.querySelector('#CartDrawer') || document.querySelector('#CartDrawer')) return;
            const inner = innerOf(res[id], element);
            if (inner !== null) el.innerHTML = inner;
          })
          .catch(err => console.error('dp-cart drawer mount:', err));
      };
      return true;
    }

    function patchCartHelper() {
      patchRenderAjax();
      if (!(window.Alpine && Alpine.store && Alpine.store('xCartHelper'))) return false;
      const helper = Alpine.store('xCartHelper');
      if (helper.__dpSurgical) return true;
      helper.__dpSurgical = true;
      const originalReRender = helper.reRenderSections.bind(helper);
      helper.reRenderSections = async function (sections) {
        // Cart PAGE uses main-cart-* sections — not ours to optimize.
        if (document.getElementById('main-cart-items')) return originalReRender(sections);
        let res = sections;
        if (!res) {
          try {
            // cache-busted: the live storefront's edge can serve this GET stale
            const r = await fetch(`${window.location.pathname}?sections=${SECTION_IDS.join(',')}&_=${Date.now()}`);
            res = await r.json();
          } catch (e) {
            console.error('dp-cart reRender fetch:', e);
            return originalReRender(sections);
          }
        }
        if (!surgicalApply(res)) {
          // Layout switch (e.g. last item removed -> empty state): smooth
          // replace instead of the theme's flashing wholesale swap.
          if (res && res['cart-drawer'] && document.querySelector('#CartDrawer')) {
            return fullDrawerReplace(res);
          }
          return originalReRender(res);
        }
      };
      return true;
    }

    if (!patchCartHelper()) {
      document.addEventListener('alpine:init', () => {
        // Theme stores register during alpine:init; patch right after.
        queueMicrotask(patchCartHelper);
      });
      document.addEventListener('alpine:initialized', patchCartHelper);
    }

    function showButtonSuccess(btn) {
      const label = btn.querySelector('.dp-upsell-label');
      if (!label) return;
      const original = label.textContent;
      btn.classList.add('is-added');
      label.textContent = '✓ Added';
      setTimeout(() => {
        btn.classList.remove('is-added');
        label.textContent = original;
      }, 1800);
    }

    function showButtonError(btn, message) {
      const label = btn.querySelector('.dp-upsell-label');
      if (!label) return;
      const original = label.textContent;
      label.textContent = '✕';
      if (message) btn.title = message;
      setTimeout(() => {
        label.textContent = original;
        btn.removeAttribute('title');
      }, 2200);
    }

    async function quickAdd(form, btn) {
      btn.classList.add('is-loading');
      btn.disabled = true;
      await acquireCartLock();
      let addedProductId = null;

      try {
        // The drawer sections are requested IN the add POST (Shopify renders
        // them in the same response as the mutation). A separate follow-up
        // GET can be served a stale pre-add render by the live storefront's
        // edge cache — the item then doesn't appear until a page refresh.
        const body = new FormData(form);
        body.append('sections', SECTION_IDS.join(','));
        body.append('sections_url', window.location.pathname);
        const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body
        });
        const data = await res.json();

        if (data.status == '422' || data.status == 422) {
          showButtonError(btn, data.description || data.message || '');
          return;
        }
        addedProductId = data.product_id;

        let sections = data.sections;
        if (!sections) {
          // fallback (older responses): refetch, cache-busted
          const sectionRes = await fetch(`${window.location.pathname}?sections=${SECTION_IDS.join(',')}&_=${Date.now()}`);
          sections = await sectionRes.json();
        }
        // No drawer in the DOM yet (lazy mount hasn't landed): mount it from
        // this response. Adding from the EMPTY cart: the swap regions don't
        // exist in the empty-state layout, so surgicalApply refuses and we
        // replace the whole drawer (empty -> full layout switch). Otherwise:
        // surgical as usual — quiet, the row animation is the feedback.
        if (!document.querySelector('#CartDrawer')) mountDrawer(sections);
        else if (!surgicalApply(sections, true)) fullDrawerReplace(sections);

        const bubble = document.querySelector('#cart-icon-bubble span');
        if (window.Alpine && Alpine.store('xCartHelper') && bubble) {
          Alpine.store('xCartHelper').currentItemCount = parseInt(bubble.innerHTML) || 0;
        }
        showButtonSuccess(btn);
        document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
        // Adds from outside the drawer (e.g. showreel cards) opt into opening
        // the mini cart so the add is unmistakable.
        if (form.hasAttribute('data-dp-open-cart') && window.Alpine) {
          // Let the button's "✓ Added" state land first: opening the drawer in
          // the same frame swallows the feedback and reads as a jump. A short
          // beat turns it into one movement — press, confirm, drawer.
          const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          setTimeout(() => {
            try {
              Alpine.store('xModal').setActiveElement('cart-icon');
              Alpine.store('xMiniCart').openCart();
            } catch (err) { /* stores not ready — badge still updates */ }
          }, calm ? 0 : 260);
        }
      } catch (e) {
        console.error('dp-cart quick-add:', e);
        showButtonError(btn, '');
      } finally {
        window.updatingCart = false;
        // Undim on the next frame so the fresh content fades back in.
        requestAnimationFrame(() => setDimmed(false));
        btn.classList.remove('is-loading');
        btn.disabled = false;
        if (addedProductId) {
          document.dispatchEvent(new CustomEvent(`eurus:product-card:clear:${addedProductId}`));
        }
      }
    }

    document.addEventListener('submit', (e) => {
      const form = e.target.closest && e.target.closest('.dp-upsell-form');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('.dp-upsell-add');
      if (!btn || btn.classList.contains('is-loading')) return;
      quickAdd(form, btn);
    }, true);

    // Optimistic remove feedback: the theme debounces removals by 500ms + network,
    // so fade the row the instant the trash icon is clicked. The row disappears
    // for real when the surgical swap lands.
    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('.dp-remove-btn');
      if (!btn || !btn.closest('#dp-cart-items-region')) return;
      btn.closest('tr')?.classList.add('dp-item-removing');
    }, true);
  })();
}
